import { Pencil, Trash2 } from "lucide-react";
import React from "react";
import { Button } from "../../../../../components/ui/common/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/ui/common/tooltip";
import { SelectOptionList, TransportRoute } from "../types/chemicalRecords.types";
import EditRouteForm from "./EditRouteForm";

interface ChemicalRouteRowProps {
  route: TransportRoute;
  chemId: string;
  transportOptions: SelectOptionList;
  transportLabelMap: Record<string, string>;
  isEditing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onUpdateRoute: (chemId: string, routeId: string, patch: Partial<TransportRoute>) => void;
  onRemoveRoute: (chemId: string, routeId: string) => void;
}

/** One transport route: a read strip, or (while editing) the boxed edit form. */
const ChemicalRouteRow: React.FC<ChemicalRouteRowProps> = ({
  route,
  chemId,
  transportOptions,
  transportLabelMap,
  isEditing,
  onEdit,
  onDone,
  onUpdateRoute,
  onRemoveRoute
}) => {
  if (isEditing) {
    return (
      <EditRouteForm
        route={route}
        chemId={chemId}
        transportOptions={transportOptions}
        onUpdateRoute={onUpdateRoute}
        onDone={onDone}
      />
    );
  }

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-py-1 tw-px-2 tw-rounded tw-bg-muted/30 tw-border tw-border-border/50">
      <div className="tw-flex-1 tw-flex tw-items-center tw-gap-2 tw-text-[10px]">
        <span className="tw-font-medium tw-text-foreground">
          {(route.type && transportLabelMap[route.type]) ?? route.type ?? "—"}
        </span>
        <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
          {route.distance || 0} km
        </span>
      </div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="tw-h-6 tw-w-6 tw-text-muted-foreground hover:tw-text-foreground"
              onClick={onEdit}
            >
              <Pencil className="tw-h-2.5 tw-w-2.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p className="tw-text-xs">Edit</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button
        variant="ghost"
        size="icon"
        className="tw-h-6 tw-w-6 tw-text-destructive"
        onClick={() => onRemoveRoute(chemId, route.id)}
      >
        <Trash2 className="tw-h-2.5 tw-w-2.5" />
      </Button>
    </div>
  );
};

export default ChemicalRouteRow;
